import { HelpAnchorInfo } from '../utils/collect-help-anchors.util';

export interface WaitForAnchorOptions {
  /** How long to wait for the anchor before giving up. Defaults to 5000ms. */
  timeoutMs?: number;
}

/**
 * The platform's answer to "which view is this step talking about, and what kind
 * of thing is it".
 *
 * On the web an anchor is a DOM element carrying a `help-path` attribute, found
 * by query. On NativeScript there are no attributes to query and no
 * MutationObserver to watch, so the directive registers views into a map keyed by
 * path — which is why resolution is a port rather than a shared selector helper.
 *
 * Candidates come from `anchorPathVariants` and are matched by
 * `anchorPathMatches`: most specific first, each one exactly or as a trailing
 * `/`-segment run.
 */
export interface IAnchorRegistry<TAnchor = unknown> {
  /** First currently-rendered anchor matching any candidate, or null. */
  resolve(pathVariants: string[]): TAnchor | null;

  /**
   * Resolve, waiting for the anchor to both exist and become visible if it does
   * not yet. Resolves null on timeout.
   *
   * Gating on visibility rather than mere existence is load-bearing: a control
   * inside a closed dialog exists but has no layout box, and spotlighting it
   * draws a zero-rect highlight over nothing.
   */
  waitFor(
    pathVariants: string[],
    options?: WaitForAnchorOptions
  ): Promise<TAnchor | null>;

  /** Every anchor currently rendered. This is the grounding handed to a Ryn that
   * generates a tour at runtime, so generated steps can only target real views. */
  inventory(): HelpAnchorInfo[];

  /**
   * Whether interacting with this anchor is what should advance the tour, for a
   * step that did not say (`step.advanceOn` unset). Inferred from the anchor
   * being an actionable control — a button on either platform.
   */
  inferTapToAdvance(anchor: TAnchor): boolean;

  /** Run `handler` when the user activates `anchor`. Returns an unbind function. */
  bindTap(anchor: TAnchor, handler: () => void): () => void;
}
